import React, { useState, useEffect, useContext, createContext } from 'react';
import api from '../auth';
import CatalogsService from '../service/CatalogsService';
import { useGroups, useMrs, useVno, useSi, useRvp, usePp, useTipTrosila, useNamenaPotrosnje, useNi, useJm, useValuta, useMarkaTrosila } from '../store';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const catalogsService = CatalogsService();

    // Access catalog stores setters
    const setGroups = useGroups((s) => s.setGroups);
    const fetchGroups = useGroups((s) => s.fetchGroups);
    const setMrss = useMrs((s) => s.setMrss);
    const fetchMrss = useMrs((s) => s.fetchMrss);
    const setVnos = useVno((s) => s.setVnos);
    const fetchVnos = useVno((s) => s.fetchVnos);
    const setSis = useSi((s) => s.setSis);
    const fetchSis = useSi((s) => s.fetchSis);
    const setRvps = useRvp((s) => s.setRvps);
    const fetchRvps = useRvp((s) => s.fetchRvps);
    const setDistributer = usePp((s) => s.setDistributer);
    const fetchDistributer = usePp((s) => s.fetchDistributer);
    const setTipTrosilas = useTipTrosila((s) => s.setTipTrosilas);
    const fetchTipTrosilas = useTipTrosila((s) => s.fetchTipTrosilas);
    const setNamenaPotrosnjes = useNamenaPotrosnje((s) => s.setNamenaPotrosnjes);
    const fetchNamenaPotrosnjes = useNamenaPotrosnje((s) => s.fetchNamenaPotrosnjes);
    const setNis = useNi((s) => s.setNis);
    const fetchNis = useNi((s) => s.fetchNis);
    const setJms = useJm((s) => s.setJms);
    const fetchJms = useJm((s) => s.fetchJms);
    const setValute = useValuta((s) => s.setValute);
    const fetchValute = useValuta((s) => s.fetchValute);
    const setMarkaTrosilas = useMarkaTrosila((s) => s.setMarkaTrosilas);
    const fetchMarkaTrosilas = useMarkaTrosila((s) => s.fetchMarkaTrosilas);

    // Load user data from localStorage on mount
    useEffect(() => {
        const normalizeRoleName = (name) => (name === 'operator' ? 'operater' : name);
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        const userPermissions = localStorage.getItem('permissions');
        const userRoles = localStorage.getItem('roles');

        if (token && userData) {
            try {
                setUser(JSON.parse(userData));
                setPermissions(userPermissions ? JSON.parse(userPermissions) : []);
                const storedRoles = userRoles ? JSON.parse(userRoles) : [];
                const normalizedRoles = Array.isArray(storedRoles) ? storedRoles.map(normalizeRoleName) : [];
                // persist normalized roles back to localStorage if any changes
                if (JSON.stringify(storedRoles) !== JSON.stringify(normalizedRoles)) {
                    localStorage.setItem('roles', JSON.stringify(normalizedRoles));
                }
                setRoles(normalizedRoles);
                setAuthenticated(true);

                // IMPORTANT: Set the authorization header
                api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                // Bootstrap catalogs via delta sync (on app restore)
                (async () => {
                    try {
                        await deltaSyncCatalogs();
                    } catch (e) {
                        // non-fatal
                        console.warn('Catalogs delta sync (restore) failed:', e?.message || e);
                    }
                })();
            } catch (error) {
                console.error('Error parsing stored user data:', error);
                logout();
            }
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch fresh user data and permissions
    const fetchUserData = async () => {
        try {
            const response = await api.get('/api/user/info');
            const userData = response.data;
            const normalizeRoleName = (name) => (name === 'operator' ? 'operater' : name);

            setUser(userData.user);
            setPermissions(userData.all_permissions || []);
            const rolesArr = userData.roles?.map((role) => normalizeRoleName(role.name)) || [];
            setRoles(rolesArr);

            // Store in localStorage
            localStorage.setItem('user', JSON.stringify(userData.user));
            localStorage.setItem('permissions', JSON.stringify(userData.all_permissions || []));
            localStorage.setItem('roles', JSON.stringify(rolesArr));

            return userData;
        } catch (error) {
            console.error('Error fetching user data:', error);
            throw error;
        }
    };

    // Get current user info (simple endpoint)
    const getCurrentUser = async () => {
        try {
            return user;
        } catch (error) {
            console.error('Error fetching current user:', error);
            throw error;
        }
    };

    const login = async (email, password) => {
        try {
            // Ensure Sanctum sets the XSRF-TOKEN cookie before login
            await api.get('/sanctum/csrf-cookie');
            const response = await api.post('/api/login', { email, password });
            const { token } = response.data;

            // Store token
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            // Fetch detailed user data with permissions
            await fetchUserData();

            setAuthenticated(true);

            // Bootstrap catalogs via delta sync right after login
            try {
                await deltaSyncCatalogs();
            } catch (e) {
                console.warn('Catalogs delta sync (login) failed:', e?.message || e);
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const logout = async () => {
        try {
            await api.post('/api/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Clear all stored data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');
            localStorage.removeItem('roles');
            localStorage.removeItem('lastCatalogsSyncAt');

            // Reset state
            setUser(null);
            setPermissions([]);
            setRoles([]);
            setAuthenticated(false);

            // Clear API headers
            delete api.defaults.headers.common['Authorization'];
        }
    };

    const register = async (userData) => {
        try {
            const response = await api.post('/api/register', userData);
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    // Delta sync catalogs and update stores; persist last sync timestamp from server
    const deltaSyncCatalogs = async () => {
        const last = localStorage.getItem('lastCatalogsSyncAt');
        try {
            const payload = await catalogsService.getChanges({ since: last || undefined, embed: 'changed' });
            const { serverNow, catalogs } = payload || {};
            if (!catalogs) throw new Error('No catalogs in response');

            // Map known catalogs to their stores
            if (catalogs.groups?.changed && Array.isArray(catalogs.groups.data)) setGroups(catalogs.groups.data);
            if (catalogs.mrs?.changed && Array.isArray(catalogs.mrs.data)) setMrss(catalogs.mrs.data);
            if (catalogs.vnos?.changed && Array.isArray(catalogs.vnos.data)) setVnos(catalogs.vnos.data);
            if (catalogs.sis?.changed && Array.isArray(catalogs.sis.data)) setSis(catalogs.sis.data);
            if (catalogs.rvps?.changed && Array.isArray(catalogs.rvps.data)) setRvps(catalogs.rvps.data);
            if (catalogs.pps?.changed && Array.isArray(catalogs.pps.data)) setDistributer(catalogs.pps.data);
            if (catalogs.tip_trosilas?.changed && Array.isArray(catalogs.tip_trosilas.data)) setTipTrosilas(catalogs.tip_trosilas.data);
            if (catalogs.namena_potrosnjes?.changed && Array.isArray(catalogs.namena_potrosnjes.data)) setNamenaPotrosnjes(catalogs.namena_potrosnjes.data);
            if (catalogs.nis?.changed && Array.isArray(catalogs.nis.data)) setNis(catalogs.nis.data);
            if (catalogs.jms?.changed && Array.isArray(catalogs.jms.data)) setJms(catalogs.jms.data);
            if (catalogs.valute?.changed && Array.isArray(catalogs.valute.data)) setValute(catalogs.valute.data);
            if (catalogs.marka_trosilas?.changed && Array.isArray(catalogs.marka_trosilas.data)) setMarkaTrosilas(catalogs.marka_trosilas.data);

            // Persist new sync time
            if (serverNow) localStorage.setItem('lastCatalogsSyncAt', serverNow);
        } catch (err) {
            // Fallback: run legacy fetchers
            await Promise.allSettled([
                fetchGroups?.(),
                fetchMrss?.(),
                fetchVnos?.(),
                fetchSis?.(),
                fetchRvps?.(),
                fetchDistributer?.(),
                fetchTipTrosilas?.(),
                fetchNamenaPotrosnjes?.(),
                fetchNis?.(),
                fetchJms?.(),
                fetchValute?.(),
                fetchMarkaTrosilas?.()
            ]);
        }
    };

    // Permission checking functions
    const hasPermission = (permission) => {
        if (!permissions || permissions.length === 0) return false;
        return permissions.includes(permission);
    };

    const hasAnyPermission = (permissionArray) => {
        if (!permissions || permissions.length === 0) return false;
        return permissionArray.some((permission) => permissions.includes(permission));
    };

    const hasAllPermissions = (permissionArray) => {
        if (!permissions || permissions.length === 0) return false;
        return permissionArray.every((permission) => permissions.includes(permission));
    };

    const hasRole = (role) => {
        if (!roles || roles.length === 0) return false;
        return roles.includes(role);
    };

    const hasAnyRole = (roleArray) => {
        if (!roles || roles.length === 0) return false;
        return roleArray.some((role) => roles.includes(role));
    };

    const hasAllRoles = (roleArray) => {
        if (!roles || roles.length === 0) return false;
        return roleArray.every((role) => roles.includes(role));
    };

    // Module access helpers
    const canAccess = (module) => {
        return hasPermission(`${module}.view`);
    };

    const canPerform = (module, action) => {
        return hasPermission(`${module}.${action}`);
    };

    // Role-based helpers
    const isSuperAdmin = () => hasRole('super_admin');
    const isAdmin = () => hasAnyRole(['super_admin', 'admin']);
    const isManager = () => hasRole('manager');
    // Support new role spelling "operater" while remaining backward compatible with existing "operator"
    const isOperator = () => hasAnyRole(['operater', 'operator']);
    const isFieldWorker = () => hasRole('field_worker');
    const isViewer = () => hasRole('viewer');

    // Job-specific helpers
    const canAccessAssignedJobs = () => {
        return hasAnyRole(['operater', 'operator', 'field_worker', 'admin', 'super_admin']);
    };

    const shouldSeeOnlyOwnJobs = () => {
        return hasAnyRole(['operater', 'operator', 'field_worker']) && !hasAnyRole(['admin', 'super_admin', 'manager']);
    };

    // PKGP/PUGI/ZM specific permissions
    const canCreatePkgp = () => hasPermission('pkgp.create');
    const canUpdatePkgp = () => hasPermission('pkgp.update');
    const canDeletePkgp = () => hasPermission('pkgp.delete');
    const canStartPkgp = () => hasPermission('pkgp.start');
    const canEndPkgp = () => hasPermission('pkgp.end');
    const canPdfPkgp = () => hasPermission('pkgp.pdf');

    const canCreatePugi = () => hasPermission('pugi.create');
    const canUpdatePugi = () => hasPermission('pugi.update');
    const canDeletePugi = () => hasPermission('pugi.delete');
    const canStartPugi = () => hasPermission('pugi.start');
    const canEndPugi = () => hasPermission('pugi.end');
    const canPdfPugi = () => hasPermission('pugi.pdf');

    const canCreateZm = () => hasPermission('zm.create');
    const canUpdateZm = () => hasPermission('zm.update');
    const canDeleteZm = () => hasPermission('zm.delete');
    const canStartZm = () => hasPermission('zm.start');
    const canEndZm = () => hasPermission('zm.end');
    const canPdfZm = () => hasPermission('zm.pdf');

    // User management permissions
    const canManageUsers = () => hasPermission('users.view');
    const canCreateUsers = () => hasPermission('users.create');
    const canUpdateUsers = () => hasPermission('users.update');
    const canDeleteUsers = () => hasPermission('users.delete');

    // Role/Permission management
    const canManageRoles = () => hasPermission('roles.view');
    const canManagePermissions = () => hasPermission('permissions.view');

    // System data permissions
    const canManageArticles = () => hasPermission('artikal.view');
    const canManageMeasuringPoints = () => hasPermission('mm.view');

    // Refresh permissions (call after role/permission changes)
    const refreshPermissions = async () => {
        try {
            await fetchUserData();
        } catch (error) {
            console.error('Error refreshing permissions:', error);
        }
    };

    // Get permissions grouped by module
    const getPermissionsByModule = () => {
        if (!permissions || permissions.length === 0) return {};

        const grouped = {};
        permissions.forEach((permission) => {
            const parts = permission.split('.');
            if (parts.length >= 2) {
                const module = parts[0];
                const action = parts[1];

                if (!grouped[module]) {
                    grouped[module] = [];
                }
                grouped[module].push(action);
            }
        });

        return grouped;
    };

    const value = {
        // Core auth
        user,
        permissions,
        roles,
        loading,
        authenticated,
        login,
        logout,
        register,
        refreshPermissions,
        getCurrentUser,

        // Permission checking
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        canAccess,
        canPerform,
        getPermissionsByModule,

        // Role helpers
        isSuperAdmin,
        isAdmin,
        isManager,
        isOperator,
        isFieldWorker,
        isViewer,

        // Job helpers
        canAccessAssignedJobs,
        shouldSeeOnlyOwnJobs,

        // PKGP permissions
        canCreatePkgp,
        canUpdatePkgp,
        canDeletePkgp,
        canStartPkgp,
        canEndPkgp,
        canPdfPkgp,

        // PUGI permissions
        canCreatePugi,
        canUpdatePugi,
        canDeletePugi,
        canStartPugi,
        canEndPugi,
        canPdfPugi,

        // ZM permissions
        canCreateZm,
        canUpdateZm,
        canDeleteZm,
        canStartZm,
        canEndZm,
        canPdfZm,

        // User management
        canManageUsers,
        canCreateUsers,
        canUpdateUsers,
        canDeleteUsers,

        // Role/Permission management
        canManageRoles,
        canManagePermissions,

        // System data
        canManageArticles,
        canManageMeasuringPoints,

        // catalogs
        deltaSyncCatalogs
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};

export { useAuth, AuthProvider };
